/** GPU Radial Blur — single-pass spin / zoom averaging.
 *
 *  Fragment-shader port of `lib/filters/radial-blur.ts`. Per
 *  output pixel, we average N source samples obtained by rotating
 *  (spin) or scaling (zoom) the pixel's offset from the user's
 *  in-canvas center point. The two modes share dispatch shape but
 *  use separate shaders so each one's inner loop stays branch-free.
 *
 *  CPU pre-computes the sample table (cos/sin pairs for spin;
 *  scales for zoom) and the sample count from `amount`. The
 *  shader's job is the per-pixel N-iteration accumulation + mean.
 *
 *  MAX_SAMPLES = 32 matches `sampleCountFromAmount`'s cap. Calls
 *  exceeding the cap fall back to CPU — currently unreachable from
 *  the UI but defensive against programmatic callers.
 *
 *  Identity short-circuit at `amount === 0` mirrors the JS path.
 *
 *  ── Y-axis convention ─────────────────────────────────────────
 *  px is computed in CPU-aligned top-down coordinates (px.y = 0 at
 *  DOM top); sample UVs flip Y back when forming the texture-read
 *  coord so the `UNPACK_FLIP_Y` source is sampled at the matching
 *  DOM row. Spin requires this — rotation chirality depends on
 *  which way `dy` points. Zoom is radially symmetric and would
 *  produce the same output either way, but the flip is applied
 *  for consistency with the spin shader. See twirl.ts header for
 *  the full rationale. */

import type { RadialBlurValue } from '../../filters/radial-blur';
import { applyIdentityCopy } from '../identity-copy';
import { applyShader } from '../runtime';

const MAX_SAMPLES = 32;

/** Spin shader. Each sample rotates the pixel's center-relative
 *  offset by a small angle (cosT[s], sinT[s]) and reads the source
 *  at the rotated position. Rounding + clamp mirrors the JS path's
 *  `Math.round` + bounds check. */
const SPIN_FRAG = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_source;
uniform vec2 u_dims;
uniform vec2 u_center;
uniform float u_cosT[${MAX_SAMPLES}];
uniform float u_sinT[${MAX_SAMPLES}];
uniform int u_samples;
out vec4 outColor;

void main() {
  // px in CPU-aligned top-down coords (px.y = 0 at DOM top).
  float pxX = v_uv.x * u_dims.x - 0.5;
  float pxY = (1.0 - v_uv.y) * u_dims.y - 0.5;
  float dx = pxX - u_center.x;
  float dy = pxY - u_center.y;
  vec3 sum = vec3(0.0);
  vec4 center = texture(u_source, v_uv);
  for (int s = 0; s < ${MAX_SAMPLES}; s++) {
    if (s >= u_samples) break;
    float sxF = u_center.x + dx * u_cosT[s] - dy * u_sinT[s];
    float syF = u_center.y + dx * u_sinT[s] + dy * u_cosT[s];
    // floor(x + 0.5) matches Math.round for ties → +inf, which is
    // what JS's Math.round does. Clamp to source bounds.
    float sxClamped = clamp(floor(sxF + 0.5), 0.0, u_dims.x - 1.0);
    float syClamped = clamp(floor(syF + 0.5), 0.0, u_dims.y - 1.0);
    // syClamped is in top-down coords; flip Y in the sampling UV so
    // the UNPACK_FLIP_Y source is read at the matching DOM row.
    vec2 uv = vec2(
      (sxClamped + 0.5) / u_dims.x,
      1.0 - (syClamped + 0.5) / u_dims.y
    );
    sum += texture(u_source, uv).rgb;
  }
  outColor = vec4(sum / float(u_samples), center.a);
}
`;

/** Zoom shader. Each sample scales the pixel's center-relative
 *  offset by `scales[s]` (centered on 1, symmetric range around
 *  1±maxRange) and reads at the scaled position. */
const ZOOM_FRAG = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_source;
uniform vec2 u_dims;
uniform vec2 u_center;
uniform float u_scales[${MAX_SAMPLES}];
uniform int u_samples;
out vec4 outColor;

void main() {
  // Same top-down px convention as the spin shader — see
  // file header for the rationale.
  float pxX = v_uv.x * u_dims.x - 0.5;
  float pxY = (1.0 - v_uv.y) * u_dims.y - 0.5;
  float dx = pxX - u_center.x;
  float dy = pxY - u_center.y;
  vec3 sum = vec3(0.0);
  vec4 center = texture(u_source, v_uv);
  for (int s = 0; s < ${MAX_SAMPLES}; s++) {
    if (s >= u_samples) break;
    float sxF = u_center.x + dx * u_scales[s];
    float syF = u_center.y + dy * u_scales[s];
    float sxClamped = clamp(floor(sxF + 0.5), 0.0, u_dims.x - 1.0);
    float syClamped = clamp(floor(syF + 0.5), 0.0, u_dims.y - 1.0);
    vec2 uv = vec2(
      (sxClamped + 0.5) / u_dims.x,
      1.0 - (syClamped + 0.5) / u_dims.y
    );
    sum += texture(u_source, uv).rgb;
  }
  outColor = vec4(sum / float(u_samples), center.a);
}
`;

/** Mirror `sampleCountFromAmount` from the CPU path exactly so the
 *  GPU and JS paths use the same sample count for any given amount. */
function sampleCountFromAmount(amount: number): number {
  return Math.max(4, Math.round(4 + (amount / 100) * 28));
}

/** Pad an array to MAX_SAMPLES so the uniform array's length
 *  matches the shader's declared array size. Trailing values are
 *  unused because the shader's `if (s >= u_samples) break;` cutoff
 *  skips them. */
function padSamples(values: Float32Array): number[] {
  const padded = new Array<number>(MAX_SAMPLES).fill(0);
  for (let i = 0; i < values.length; i++) padded[i] = values[i];
  return padded;
}

export function applyRadialBlurOnGpu(
  source: ImageData,
  target: HTMLCanvasElement,
  value: RadialBlurValue,
): boolean {
  const amount = Math.max(0, Math.min(100, value.amount));
  if (amount === 0) return applyIdentityCopy(source, target);
  const w = source.width;
  const h = source.height;
  const cx = value.centerX * w;
  const cy = value.centerY * h;
  const samples = sampleCountFromAmount(amount);
  if (samples > MAX_SAMPLES) return false;
  const dims: [number, number] = [w, h];

  if (value.mode === 'spin') {
    const maxAngleDeg = (amount / 100) * 30;
    const maxAngle = (maxAngleDeg * Math.PI) / 180;
    const cosT = new Float32Array(samples);
    const sinT = new Float32Array(samples);
    for (let s = 0; s < samples; s++) {
      const t = -maxAngle + (2 * maxAngle * s) / (samples - 1);
      cosT[s] = Math.cos(t);
      sinT[s] = Math.sin(t);
    }
    return applyShader({
      source,
      target,
      fragShaderSrc: SPIN_FRAG,
      uniforms: {
        u_dims: { type: 'vec2', value: dims },
        u_center: { type: 'vec2', value: [cx, cy] },
        u_cosT: { type: 'float[]', value: padSamples(cosT) },
        u_sinT: { type: 'float[]', value: padSamples(sinT) },
        u_samples: { type: 'int', value: samples },
      },
    });
  }

  // Zoom mode.
  const maxRange = (amount / 100) * 0.3;
  const scales = new Float32Array(samples);
  for (let s = 0; s < samples; s++) {
    scales[s] = 1 - maxRange + (2 * maxRange * s) / (samples - 1);
  }
  return applyShader({
    source,
    target,
    fragShaderSrc: ZOOM_FRAG,
    uniforms: {
      u_dims: { type: 'vec2', value: dims },
      u_center: { type: 'vec2', value: [cx, cy] },
      u_scales: { type: 'float[]', value: padSamples(scales) },
      u_samples: { type: 'int', value: samples },
    },
  });
}
